import { Link } from "@tanstack/react-router";
import { Facebook, Instagram } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { SITE_SETTING_DEFAULTS } from "@/lib/site-settings.functions";

export function SiteFooter() {
  const { data: settings } = useQuery({
    queryKey: ["site-settings"],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.from("site_settings").select("key, value");
      if (error) return { ...SITE_SETTING_DEFAULTS };
      const map: Record<string, string> = { ...SITE_SETTING_DEFAULTS };
      for (const row of data ?? []) map[row.key] = row.value;
      return map;
    },
  });
  const facebookUrl = settings?.facebook_url ?? SITE_SETTING_DEFAULTS.facebook_url;
  const instagramUrl = settings?.instagram_url ?? SITE_SETTING_DEFAULTS.instagram_url;

  return (
    <footer className="border-t bg-card mt-16">
      <div className="container py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
          <div>
            <h4 className="font-display font-bold text-sm mb-3">Jackpots</h4>
            <div className="flex flex-col gap-2 text-sm text-muted-foreground">
              <Link to="/games/$gameSlug" params={{ gameSlug: "powerball" }} className="hover:text-foreground">Powerball</Link>
              <Link to="/games/$gameSlug" params={{ gameSlug: "mega-millions" }} className="hover:text-foreground">Mega Millions</Link>
              <Link to="/games/$gameSlug" params={{ gameSlug: "lucky-for-life" }} className="hover:text-foreground">Lucky for Life</Link>
              <Link to="/powerball/how-to-play" className="hover:text-foreground">How to Play Powerball</Link>
              <Link to="/mega-millions/how-to-play" className="hover:text-foreground">How to Play Mega Millions</Link>
            </div>
          </div>
          <div>
            <h4 className="font-display font-bold text-sm mb-3">Tools</h4>
            <div className="flex flex-col gap-2 text-sm text-muted-foreground">
              <Link to="/tools/number-generator" className="hover:text-foreground">Number Generator</Link>
              <Link to="/tools/results-checker" className="hover:text-foreground">Results Checker</Link>
              <Link to="/tools/tax-calculator" className="hover:text-foreground">Tax Calculator</Link>
              <Link to="/tools/frequency-analyzer" className="hover:text-foreground">Frequency Analyzer</Link>
              <Link to="/tools/payout-calculator" className="hover:text-foreground">Payout Calculator</Link>
              <Link to="/powerball/jackpot-analysis" className="hover:text-foreground">Powerball Payout Analysis</Link>
              <Link to="/mega-millions/jackpot-analysis" className="hover:text-foreground">Mega Millions Payout Analysis</Link>
            </div>
          </div>
          <div>
            <h4 className="font-display font-bold text-sm mb-3">Popular States</h4>
            <div className="flex flex-col gap-2 text-sm text-muted-foreground">
              <Link to="/states/$stateSlug" params={{ stateSlug: "new-york" }} className="hover:text-foreground">New York</Link>
              <Link to="/states/$stateSlug" params={{ stateSlug: "california" }} className="hover:text-foreground">California</Link>
              <Link to="/states/$stateSlug" params={{ stateSlug: "texas" }} className="hover:text-foreground">Texas</Link>
              <Link to="/states/$stateSlug" params={{ stateSlug: "florida" }} className="hover:text-foreground">Florida</Link>
            </div>
          </div>
          <div>
            <h4 className="font-display font-bold text-sm mb-3">Info</h4>
            <div className="flex flex-col gap-2 text-sm text-muted-foreground">
              <Link to="/results" className="hover:text-foreground">Results</Link>
              <Link to="/my-picks" className="hover:text-foreground">My Picks</Link>
              <Link to="/states" className="hover:text-foreground">All States</Link>
            </div>
          </div>
        </div>

        <div className="border-t pt-6 space-y-3">
          <div className="flex items-center gap-3">
            <span className="text-xs font-medium text-muted-foreground">Follow us:</span>
            <a
              href={facebookUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="USA Lottery Club on Facebook"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border text-muted-foreground transition-colors hover:border-accent hover:text-accent"
            >
              <Facebook className="h-4 w-4" />
            </a>
            <a
              href={instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="USA Lottery Club on Instagram"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border text-muted-foreground transition-colors hover:border-accent hover:text-accent"
            >
              <Instagram className="h-4 w-4" />
            </a>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            <strong>Disclaimer:</strong> USA Lottery Club is an independent informational website and is not affiliated with, endorsed by, or connected to any state lottery commission or lottery organization. Lottery results and jackpot amounts are for informational purposes only. Always verify results with official state lottery websites.
          </p>
          <p className="text-xs text-muted-foreground">
            <strong>Responsible Gaming:</strong> Lottery games are games of chance. Please play responsibly. If you or someone you know has a gambling problem, call 1-800-GAMBLER.
          </p>
          <p className="text-xs text-muted-foreground">
            Must be 18+ to play (21+ in some states). © {new Date().getFullYear()} USA Lottery Club. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}